import { RequestAnalysis, GroundingState } from "../../../models/intake";
import { RequestType, DISABILITY_OR_SOCIAL_SECURITY } from "../../../models/requestType";

/**
 * Client-side FALLBACK resolution of the AI-extracted request type / purpose free text against the
 * live catalog.
 *
 * ROIS used to own this (`RequestTypePurposeResolver`, AB#34097 / F2). Commit bb82963 deleted it and
 * moved the responsibility to the Intake API, which returns resolved enum ids on
 * `requestTypeMapped` / `requestPurposeMapped`. Nothing was left behind for the case where the API
 * returns NO ids - an API build predating that feature, or any resolve failure - and every reader in
 * Quick Entry keys off those ids: the seed, the dropdowns, the row label, and the red
 * "AI found '<type>' - not a listed type" hint. So a plainly-listed type rendered as unlisted with
 * both dropdowns on "- Select -".
 *
 * The API stays authoritative: when it supplied a type id this returns the analysis object
 * UNTOUCHED, by reference. The fallback only fills a gap.
 *
 * Matching rules are ported verbatim from the deleted resolver so the two can never disagree -
 * normalized-exact (case-insensitive, internal whitespace collapsed), exactly one match required, a
 * purpose only when it is a child of the resolved type, and Disability/SSA deferring to the
 * barcode-reader workflow. DELIBERATE DUPLICATE of the Intake API's
 * RequestTypePurposeCatalogMapper; keep the two in step.
 *
 * Never logs a value - extracted request strings are PHI-adjacent (.claude/rules/hipaa-compliance.md).
 */

// Collapse internal whitespace runs and trim, so "  Records   Request " matches "Records Request".
function normalize(value: string): string {
    return value.trim().split(/\s+/).join(" ").toLowerCase();
}

function normalizedEquals(catalogName: string | undefined, aiValue: string): boolean {
    return typeof catalogName === "string" && normalize(catalogName) === normalize(aiValue);
}

/**
 * AB#35076: whether an API-supplied `requestPurposeMapped` may be auto-selected, or whether it is a
 * value the extraction INFERRED and must not present as confirmed.
 *
 * QA reported three documents where a purpose the document never mentioned was auto-selected and the
 * row went green: "Insurance / Attorney" became Insurance / Claim, "Legal / Medicare-Medicaid" became
 * Legal / Investigation, "Patient / Subpoena" became Patient / Patient Request.
 *
 * Two things that look like they should detect this and do NOT:
 *
 *  1. Catalog membership. Every substitute IS a valid child of its type - Claim really is one of
 *     Insurance's purposes - so a membership check passes all three.
 *  2. The extracted TEXT. The API does not pass through what the document said; it reports the
 *     RESOLVED name. Captured payload for the Insurance/Attorney document, whose Details table reads
 *     "Request Purpose: Attorney":
 *         "requestPurpose": "Claim",  "requestPurposeMapped": 12,  "requestPurposeUncertain": false
 *     Comparing that text to the catalog name compares "Claim" to "Claim" and can never fail. Nor
 *     does the API's own uncertainty flag help - it says false, and the write path already nulls the
 *     id whenever the extraction reports it unmapped, so any purpose reaching us is one the API
 *     claims to have resolved successfully.
 *
 * The signal that DOES discriminate is the per-field grounding the same response already carries, and
 * which nothing in the client consumed until now. For that document:
 *     "confidence": { "requestType": 0, "requestPurpose": 2 }
 *     "fieldGroundings": [ { "field": "requestType", ... } ]   <- no requestPurpose entry
 * GroundingState 2 is InferredNoSource: the extraction is stating outright that it inferred the
 * purpose and found no source for it in the document, while the type (0, Grounded) has real page
 * coordinates. So refuse an inferred purpose and let the row fall to its purpose-missing state, which
 * asks the specialist to choose - an inferred value is a suggestion, not an extraction. That state is
 * NeedsEntry, red and un-confirmable, NOT amber (review threads 53055/53056).
 *
 * Membership is still required, which keeps the separate out-of-family case guarded.
 *
 * `confidence` is read-time only and absent on the persisted re-read path (AC11), so an undefined
 * grounding cannot be treated as "inferred" - that would strip a legitimately grounded purpose from
 * every reopened transaction.
 *
 * An earlier version of this docblock justified that by claiming the live path refuses the inferred
 * value "before it can ever be persisted". That was FALSE (review thread 53134, David Heifetz): this
 * guard only keeps the value out of the FORM, while ROIS writes the id to the intake record at
 * extract time regardless. With confidence absent on reopen, the persisted id was then trusted again
 * - one browser refresh undid the whole fix. The refusal that actually holds is server-side, where
 * ResolvingRequestAnalysisRetriever now nulls RequestPurposeId at WRITE time for an InferredNoSource
 * purpose. This client check is the live-tick backstop, not the guarantee.
 */
export function isMappedPurposeUsable(
    requestPurposeMapped: number | undefined,
    type: RequestType | undefined,
    purposeConfidence: GroundingState | undefined,
): boolean {
    if (!requestPurposeMapped || !type) {
        return false;
    }
    // The extraction admits it invented this one - never auto-select it.
    if (purposeConfidence === GroundingState.InferredNoSource) {
        return false;
    }
    // Membership: the id must be one of THIS type's children. Read from type.requestPurpose, never
    // purpose.typeID - that field is deprecated and not always set by the API.
    return (type.requestPurpose ?? []).some(candidate => candidate.id === requestPurposeMapped);
}

/** Last (analysis, catalog) pair and what it produced, so a resolved result keeps a stable identity. */
let lastAnalysis: RequestAnalysis | undefined;
let lastCatalog: readonly RequestType[] | undefined;
let lastResult: RequestAnalysis | undefined;

export function withResolvedTypePurpose(
    analysis: RequestAnalysis | undefined,
    catalog: readonly RequestType[] | undefined,
): RequestAnalysis | undefined {
    const data = analysis?.data;
    // Nothing to do: no analysis, no extracted type, or the API already resolved one. The API is
    // authoritative - a local guess must never override it.
    if (!analysis || !data?.requestType || data.requestTypeMapped != null) {
        return analysis;
    }
    // The catalog loads asynchronously and starts as []; resolving against it would be a guaranteed
    // miss, and "not loaded" must not be mistaken for "not listed".
    if (!catalog || catalog.length === 0) {
        return analysis;
    }

    // mapStateToProps runs on every dispatch - without this a resolved analysis would be a new
    // object each time and re-render the pane endlessly.
    if (analysis === lastAnalysis && catalog === lastCatalog) {
        return lastResult;
    }

    const aiType = data.requestType;
    const matchedTypes = catalog.filter(type => normalizedEquals(type.name, aiType));
    // A type string that does not resolve to EXACTLY one catalog entry stays unresolved: a near or
    // ambiguous match would silently pre-select the wrong request type, which is worse than the
    // red hint telling the specialist to choose.
    if (matchedTypes.length !== 1) {
        return analysis;
    }

    const type = matchedTypes[0];
    // Disability/SSA's purpose is driven by the barcode-reader workflow, so defer rather than
    // pre-select ids for it (ported rule).
    if (type.id === DISABILITY_OR_SOCIAL_SECURITY) {
        return analysis;
    }

    // A purpose resolves only as a child of the resolved type; otherwise the type stands alone and
    // the amber "purpose-uncertain" prompt asks the specialist to pick one.
    const aiPurpose = data.requestPurpose;
    const matchedPurposes = aiPurpose
        ? (type.requestPurpose ?? []).filter(purpose => normalizedEquals(purpose.name, aiPurpose))
        : [];

    const purposeMapped = matchedPurposes.length === 1 ? matchedPurposes[0].id : undefined;
    const resolved: RequestAnalysis = {
        ...analysis,
        data: {
            ...data,
            requestTypeMapped: type.id,
            requestPurposeMapped: purposeMapped,
            // AB#35076: a resolved type with no valid child purpose must raise the same flag ROIS's
            // read path derives server-side (purposeUncertain = typeMapped && !purposeMapped).
            // Leaving it unset is what let "Insurance / Attorney" render as a green, confirmed row
            // over an empty Purpose - that flag was the row's only signal that a purpose was still
            // missing. Preserved rather than overwritten when it is already set.
            requestPurposeUncertain: data.requestPurposeUncertain || purposeMapped === undefined,
        },
    };

    lastAnalysis = analysis;
    lastCatalog = catalog;
    lastResult = resolved;
    return resolved;
}

export default withResolvedTypePurpose;

/**
 * Whether confirming the Request Type/Purpose row should push the purpose to the backend
 * (POST transactions/purpose + the transactionDetails refresh that follows it).
 *
 * AB#35152 rework: that round-trip used to fire from the field's onRequestPurposeChange, i.e. on
 * every change while the row was still open for edit. It clears `quickEntryReleaseInfo` - throwing
 * away the specialist's Records Requested selections - and refreshes transactionDetails, which
 * resets other rows. Cancel could not undo any of it, because it had already happened, which
 * defeats the entire point of Cancel. It now fires from Confirm instead.
 *
 * Guarded on an actual change so that opening a row, touching nothing and confirming is free -
 * otherwise every Confirm would still wipe Records Requested. Compared numerically because a
 * dropdown pick arrives via parseInt while rehydration writes the id straight from the transaction;
 * a string/number difference is not a change. The "- Select -" sentinel (negative), 0 and "" are
 * not purposes and are never pushed.
 */
export function shouldSyncRequestPurpose(
    confirmedPurpose: number | undefined,
    persistedPurpose: number | undefined,
): boolean {
    const next = Number(confirmedPurpose);
    if (!Number.isFinite(next) || next <= 0) {
        return false;
    }
    return next !== Number(persistedPurpose);
}
